import { SelectOptionValue } from '@acin/ui-core/types/src/components/Select/components/Select';
import React from 'react';
import {
  IInternalRegulationDocumentInstance,
  IInternalRegulationDocumentReviewStatus,
} from '../../../graphql/generated';

export const useDocumentFilters = (
  statusField: 'obligationReviewStatus' | 'mappingsReviewStatus' = 'obligationReviewStatus',
) => {
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [status, setStatus] = React.useState<IInternalRegulationDocumentReviewStatus | null>(null);
  const [date, setDate] = React.useState<string | null>(null);

  const handleSearch = (input: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(input.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleStatusChange = (value: SelectOptionValue) => {
    setStatus(value === 'ALL' ? null : (value as IInternalRegulationDocumentReviewStatus));
  };

  const handleDateChange = (value: SelectOptionValue) => {
    setDate(value === 'ALL' ? null : String(value));
  };

  const getFilteredDocuments = React.useCallback(
    (documents: IInternalRegulationDocumentInstance[]) =>
      filterDocuments(documents, searchTerm, status, date, statusField),
    [searchTerm, status, date, statusField],
  );

  return {
    searchTerm,
    status,
    date,
    handleSearch,
    handleClearSearch,
    handleStatusChange,
    handleDateChange,
    getFilteredDocuments,
  };
};

const normalizeString = (str: string | null | undefined) => {
  if (!str) return '';

  return str.toLowerCase().replaceAll('/', '');
};

const filterDocuments = (
  documents: IInternalRegulationDocumentInstance[],
  searchTerm: string,
  status: IInternalRegulationDocumentReviewStatus | null,
  date: string | null,
  statusField: 'obligationReviewStatus' | 'mappingsReviewStatus' = 'obligationReviewStatus',
): IInternalRegulationDocumentInstance[] => {
  if (!documents?.length) return [];
  if (!searchTerm && !status && !date) return documents;

  const normalizedSearchTerm = normalizeString(searchTerm);

  return documents.filter((doc) => {
    const termInId = normalizeString(doc.citation).includes(normalizedSearchTerm);
    const termInName = normalizeString(doc.name).includes(normalizedSearchTerm);

    const matchesSearch = termInId || termInName;
    const docStatus = statusField === 'mappingsReviewStatus' ? doc.mappingsReviewStatus : doc.obligationReviewStatus;
    const matchesStatus = status ? docStatus === status : true;
    const matchesDate = date ? doc.createdTimestamp === new Date(Number(date)).toISOString().split('T')[0] : true;

    return matchesSearch && matchesStatus && matchesDate;
  });
};
